import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === "ja" ? "en" : "ja";
    i18n.changeLanguage(newLang);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <a href="#hero" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-run-line text-white text-xl"></i>
              </div>
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>
                J-Motion Academy
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection("concept")} className={`transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                {t("nav_concept")}
              </button>
              <button onClick={() => scrollToSection("features")} className={`transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                {t("nav_features")}
              </button>
              <button onClick={() => scrollToSection("details")} className={`transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                {t("nav_details")}
              </button>
              <button onClick={() => scrollToSection("content")} className={`transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                {t("nav_content")}
              </button>
              <button onClick={() => scrollToSection("instructor")} className={`transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                {t("nav_instructor")}
              </button>
              <button onClick={() => scrollToSection("faq")} className={`transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                {t("nav_faq")}
              </button>
              <button onClick={() => scrollToSection("register")} className="bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                {t("nav_register")}
              </button>
              
              {/* Contact Button */}
              <a
                href="mailto:mariharuno@gmail.com"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                  isScrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-mail-line text-lg"></i>
                <span className="text-sm font-medium">{t('nav_contact') || 'Contact'}</span>
              </a>

              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className={`transition-colors cursor-pointer whitespace-nowrap font-medium ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
                }`}
              >
                {i18n.language === "ja" ? "English" : "日本語"}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-3">
              {/* Mobile Contact Button */}
              <a 
                href="mailto:mariharuno@gmail.com" 
                className={`transition-colors cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
                }`}
                title="Contact Us"
              >
                <i className="ri-mail-line text-2xl"></i>
              </a>
              
              {/* Mobile Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
                }`}
              >
                {i18n.language === "ja" ? "EN" : "日本語"}
              </button>
              
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`transition-colors cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
                }`}
              >
                <i className={`${isMenuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
        isMenuOpen ? 'visible' : 'invisible'
      }`}>
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMenuOpen(false)}
        ></div>
        <div className={`absolute right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            <nav className="flex-1 px-6 py-8 space-y-2 overflow-y-auto">
              <button onClick={() => scrollToSection("concept")} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer">
                {t("nav_concept")}
              </button>
              <button onClick={() => scrollToSection("features")} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer">
                {t("nav_features")}
              </button>
              <button onClick={() => scrollToSection("details")} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer">
                {t("nav_details")}
              </button>
              <button onClick={() => scrollToSection("content")} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer">
                {t("nav_content")}
              </button>
              <button onClick={() => scrollToSection("instructor")} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer">
                {t("nav_instructor")}
              </button>
              <button onClick={() => scrollToSection("faq")} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer">
                {t("nav_faq")}
              </button>
              <button onClick={() => scrollToSection("register")} className="block w-full text-left px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 transition-colors cursor-pointer rounded-lg">
                {t("nav_register")}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}
